import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlarmClock, ArrowLeft, Bell, Bot, ExternalLink, Loader2, MessageCircle } from 'lucide-react';
import tenantWebSocketService from '../../services/tenantWebSocketService';
import alertsAlarmService from '../../services/alertsAlarmService';
import soundAlertService from '../../services/soundAlertService';
import { getConversationMessages } from '../../services/chatService';
import { extractMessagesArray, extractPaginationInfo } from '../../lib/state/hooks/chatQueries/transformers';
import { SOUND_TYPES } from '../../constants/soundConstants';
import { getAvatarColor } from '../chat/components/Avatar';
import { ACTIVE_TABS } from '../chat/types';
import { showError, showSuccess } from '../../utils/toast';

const sortMessagesByCreatedAt = (items) =>
  [...items].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

const normalizeCardItem = (raw) => {
  if (!raw || raw.card_id == null || raw.conversation_id == null) return null;
  return {
    cardId: raw.card_id,
    conversationId: raw.conversation_id,
    customerName: raw.customer_name || 'Customer',
    channel: raw.channel || '',
    alertsEnteredAt: raw.alerts_entered_at || null,
    nextAlarmAt: raw.next_alarm_at || null,
    message: raw.message || 'This conversation is waiting in Alerts and needs attention.',
  };
};

const normalizeAlarmEvent = (payload) => {
  const rawCards = Array.isArray(payload?.cards) ? payload.cards : [];
  return {
    cards: rawCards.map(normalizeCardItem).filter(Boolean),
    expiresAt: payload?.expires_at || null,
    ringSeconds: payload?.ring_seconds != null ? Number(payload.ring_seconds) : null,
  };
};

const toPreviewCard = (cardItem) => ({ ...cardItem, messages: [], hasMoreMessages: false });

const isCardDismissed = (card, dismissedList) =>
  dismissedList.some((d) => {
    const dCardId = d.card_id ?? d.cardId;
    const dConvId = d.conversation_id ?? d.conversationId;
    return (
      (dCardId != null && String(dCardId) === String(card.cardId)) ||
      (dConvId != null && String(dConvId) === String(card.conversationId))
    );
  });

const getRemainingSeconds = (expiresAt) => {
  if (!expiresAt) return null;
  const diffMs = new Date(expiresAt).getTime() - Date.now();
  if (Number.isNaN(diffMs)) return null;
  return Math.max(0, Math.round(diffMs / 1000));
};

const formatCountdown = (totalSeconds) => {
  if (totalSeconds == null) return '--:--';
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

const getInitial = (name) => (name || '?').trim().charAt(0).toUpperCase() || '?';

const formatMessageTime = (createdAt) => {
  if (!createdAt) return '';
  const date = new Date(createdAt);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
};

const formatTimeOfDay = formatMessageTime;

const formatUnattendedMinutes = (alertsEnteredAt, nowMs) => {
  if (!alertsEnteredAt) return null;
  const enteredMs = new Date(alertsEnteredAt).getTime();
  if (Number.isNaN(enteredMs)) return null;
  return Math.max(0, Math.floor((nowMs - enteredMs) / 60000));
};

const MessageBubble = ({ item }) => {
  const isCustomer = item.sender_type === 'Customer';
  const isBot = item.sender_type === 'Bot';
  const time = formatMessageTime(item.created_at);
  const body = item.body || item.caption || (item.media_kind ? `[${item.media_kind}]` : '');

  if (isCustomer) {
    return (
      <div className="flex items-start gap-2.5">
        <div className="shrink-0 mt-0.5">
          {item.sender_profile_pic ? (
            <img
              src={item.sender_profile_pic}
              alt=""
              className="w-7 h-7 rounded-full object-cover"
            />
          ) : (
            <div
              className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold ${getAvatarColor(
                item.sender_name
              )}`}
            >
              {getInitial(item.sender_name)}
            </div>
          )}
        </div>
        <div className="relative min-w-0 max-w-[80%] bg-gray-100 text-gray-800 text-sm rounded-md px-3 py-2 break-words">
          <span className="absolute -left-[6px] top-3 w-0 h-0 border-y-[6px] border-y-transparent border-r-[6px] border-r-gray-100" />
          <div className="flex items-center justify-between gap-3">
            <span className="text-[12px] font-semibold text-blue-600">
              {item.sender_name || 'Customer'}
            </span>
            {time && <span className="text-[11px] text-gray-400 shrink-0">{time}</span>}
          </div>
          <div className="mt-0.5 text-[13px]">{body}</div>
        </div>
      </div>
    );
  }

  const avatarLabel = item.sender_name || item.sender_type || 'Agent';
  const bubbleBgClass = isBot ? 'bg-blue-50' : 'bg-gray-100';
  const tailBorderClass = isBot ? 'border-l-blue-50' : 'border-l-gray-100';

  return (
    <div className="flex items-start gap-2.5 justify-end">
      <div className={`relative min-w-0 max-w-[80%] ${bubbleBgClass} text-gray-800 text-sm rounded-md px-3 py-2 break-words`}>
        <span
          className={`absolute -right-[5px] top-3 w-0 h-0 border-y-[6px] border-y-transparent border-l-[6px] ${tailBorderClass}`}
        />
        <div className="flex items-center justify-between gap-3">
          <span className="text-[12px] font-semibold text-blue-600">
            {item.sender_name || (isBot ? 'Bot' : item.sender_type || 'Agent')}
          </span>
          {time && <span className="text-[11px] text-gray-400 shrink-0">{time}</span>}
        </div>
        <div className="mt-0.5 text-[13px]">{body}</div>
      </div>
      <div className="shrink-0 mt-0.5">
        {isBot ? (
          <div className="w-7 h-7 rounded-full bg-[#E0E9FD] flex items-center justify-center">
            <Bot className="w-4 h-4 text-[#2563eb]" />
          </div>
        ) : (
          <div
            className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold ${getAvatarColor(
              avatarLabel
            )}`}
          >
            {getInitial(avatarLabel)}
          </div>
        )}
      </div>
    </div>
  );
};

const AlertCardRow = ({ card, nowTick, isSubmitting, disabled, onAccept, onOpen }) => {
  const minutes = formatUnattendedMinutes(card.alertsEnteredAt, nowTick);

  return (
    <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-100 last:border-b-0">
      <div className="flex items-center gap-3 min-w-0">
        <div className="relative shrink-0">
          <div
            className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-semibold ${getAvatarColor(
              card.customerName
            )}`}
          >
            {getInitial(card.customerName)}
          </div>
          {card.channel && (
            <span className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-green-500 border-2 border-white" />
          )}
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-gray-900 truncate">{card.customerName}</p>
          {minutes != null && (
            <p className="flex items-center gap-1 text-xs text-gray-500 mt-0.5">
              <span className="w-1.5 h-1.5 rounded-full bg-red-500 shrink-0" />
              Unattended for {minutes} min
            </p>
          )}
        </div>
      </div>
      <div className="flex flex-col items-end gap-1.5 shrink-0">
        <span className="text-[11px] text-gray-400">{formatTimeOfDay(card.alertsEnteredAt)}</span>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={onAccept}
            disabled={disabled}
            className="rounded-md border border-blue-200 bg-white px-2.5 py-1.5 text-xs font-medium text-blue-600 transition-colors hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isSubmitting ? 'Please wait...' : 'Accept Chat'}
          </button>
          <button
            type="button"
            onClick={onOpen}
            disabled={disabled}
            className="rounded-md bg-blue-600 px-2.5 py-1.5 text-xs font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            Open Chat
          </button>
        </div>
      </div>
    </div>
  );
};

const AlertNeedsAttentionModal = () => {
  const navigate = useNavigate();

  const [cards, setCards] = useState([]);
  const [topExpiresAt, setTopExpiresAt] = useState(null);
  const [arrivedAsMultiple, setArrivedAsMultiple] = useState(false);
  const [focusedCard, setFocusedCard] = useState(null);
  // "Next alarm in" display value — always driven by the socket event's ring_seconds
  // counted down locally, never by diffing expires_at (which some browsers parse/tick
  // inconsistently against the server clock).
  const [ringCountdown, setRingCountdown] = useState(null);
  const [submittingCardId, setSubmittingCardId] = useState(null);
  const [previewHasMore, setPreviewHasMore] = useState(false);
  const [previewLoadingMore, setPreviewLoadingMore] = useState(false);
  const [previewLoadingInitial, setPreviewLoadingInitial] = useState(false);
  const [nowTick, setNowTick] = useState(Date.now());

  const cardsRef = useRef([]);
  const focusedCardRef = useRef(null);
  const previewScrollRef = useRef(null);
  const previewPageRef = useRef(1);
  const previewScrollAnchorRef = useRef(null);
  const ringSecondsRef = useRef(null);
  const ringCountdownIntervalRef = useRef(null);
  const alarmMutedRef = useRef(false);

  const clearRingCountdownInterval = () => {
    if (ringCountdownIntervalRef.current) {
      clearInterval(ringCountdownIntervalRef.current);
      ringCountdownIntervalRef.current = null;
    }
  };

  // Restarts the local "Next alarm in" countdown from ring_seconds. Called on every
  // new alarm card event so a fresh event always gets a full ring_seconds countdown.
  // The modal now closes off this countdown reaching zero, not off the server's
  // expires_at — a new socket event arriving before zero simply restarts the clock.
  const startRingCountdown = (ringSeconds) => {
    clearRingCountdownInterval();
    if (ringSeconds == null || Number.isNaN(ringSeconds) || ringSeconds <= 0) {
      setRingCountdown(null);
      return;
    }
    let remaining = ringSeconds;
    setRingCountdown(remaining);
    ringCountdownIntervalRef.current = setInterval(() => {
      remaining -= 1;
      if (remaining <= 0) {
        clearRingCountdownInterval();
        setRingCountdown(0);
        closeModal();
        return;
      }
      setRingCountdown(remaining);
    }, 1000);
  };

  useEffect(() => {
    cardsRef.current = cards;
  }, [cards]);

  useEffect(() => {
    focusedCardRef.current = focusedCard;
  }, [focusedCard]);

  const closeModal = useCallback(() => {
    soundAlertService.cancelAllSounds();
    alarmMutedRef.current = false;
    clearRingCountdownInterval();
    setRingCountdown(null);
    setCards([]);
    setTopExpiresAt(null);
    setArrivedAsMultiple(false);
    setFocusedCard(null);
    setSubmittingCardId(null);
    previewPageRef.current = 1;
    previewScrollAnchorRef.current = null;
    setPreviewHasMore(false);
    setPreviewLoadingMore(false);
    setPreviewLoadingInitial(false);
  }, []);

  // Subscribe to the Tenant WebSocket events for Alert Needs Attention cards.
  useEffect(() => {
    const handleCardEvent = (payload) => {
      const { cards: nextCards, expiresAt, ringSeconds } = normalizeAlarmEvent(payload);
      if (nextCards.length === 0) return;

      const isMultiple = nextCards.length > 1;

      setTopExpiresAt(expiresAt);
      setCards(nextCards);
      setArrivedAsMultiple(isMultiple);
      setSubmittingCardId(null);

      setFocusedCard((prevFocused) => {
        if (!isMultiple) {
          // Single-card event: always jump straight into the focused (image-1) view.
          return toPreviewCard(nextCards[0]);
        }
        // Multiple-card event: keep the current focus if that card is still present,
        // otherwise fall back to the list.
        if (prevFocused) {
          const stillPresent = nextCards.find(
            (c) => String(c.cardId) === String(prevFocused.cardId)
          );
          if (stillPresent) {
            return { ...prevFocused, ...stillPresent };
          }
        }
        return null;
      });

      // New ring cycle: remember its ring length and un-mute so the repeating
      // alarm effect below keeps ringing for this event, even if a previous one was stopped.
      ringSecondsRef.current = ringSeconds;
      alarmMutedRef.current = false;
      startRingCountdown(ringSeconds);

      const soundType = isMultiple ? SOUND_TYPES.ALERTS_ALARM_MULTIPLE : SOUND_TYPES.ALERTS_ALARM_SINGLE;
      soundAlertService.playNotificationSound(soundType, null, ringSeconds);
    };

    const handleDismissedEvent = (payload) => {
      const dismissedList = Array.isArray(payload?.cards)
        ? payload.cards
        : payload?.card_id != null || payload?.conversation_id != null
        ? [{ card_id: payload.card_id, conversation_id: payload.conversation_id }]
        : [];

      const prevCards = cardsRef.current;
      if (prevCards.length === 0) return;
      // An event with no card_id/conversation_id/cards carries nothing to dismiss (e.g. a
      // ring-expiry ping) — ignore it instead of treating it as "dismiss everything".
      if (dismissedList.length === 0) return;

      const remaining = prevCards.filter((card) => !isCardDismissed(card, dismissedList));

      if (remaining.length === prevCards.length) return;

      if (remaining.length === 0) {
        closeModal();
        return;
      }

      setCards(remaining);
      const currentFocused = focusedCardRef.current;
      if (currentFocused && isCardDismissed(currentFocused, dismissedList)) {
        setFocusedCard(null);
      }
    };

    tenantWebSocketService.on('conversation.alerts_alarm_card', handleCardEvent);
    tenantWebSocketService.on('conversation.alerts_alarm_card_dismissed', handleDismissedEvent);
    // Same removal semantics as the "dismissed" event: drop the one card from the list,
    // or close the whole modal if it was the last card left.
    tenantWebSocketService.on('conversation.alerts_alarm_card_item_removed', handleDismissedEvent);

    return () => {
      tenantWebSocketService.off('conversation.alerts_alarm_card', handleCardEvent);
      tenantWebSocketService.off('conversation.alerts_alarm_card_dismissed', handleDismissedEvent);
      tenantWebSocketService.off('conversation.alerts_alarm_card_item_removed', handleDismissedEvent);
      clearRingCountdownInterval();
    };
  }, [closeModal]);

  // Keep the alarm ringing back-to-back (same ring pattern, replayed) for as long as the
  // "Next alarm in" countdown hasn't reached zero, instead of going silent after one ring.
  // Stop Sound mutes this until the next socket event starts a fresh ring cycle.
  useEffect(() => {
    if (!topExpiresAt || cards.length === 0) return undefined;

    const soundType = arrivedAsMultiple ? SOUND_TYPES.ALERTS_ALARM_MULTIPLE : SOUND_TYPES.ALERTS_ALARM_SINGLE;
    const repeatMs = soundAlertService.clampAlertsAlarmRingSeconds(ringSecondsRef.current) * 1000;

    const intervalId = setInterval(() => {
      if (alarmMutedRef.current) return;
      const remaining = getRemainingSeconds(topExpiresAt);
      if (remaining == null || remaining <= 0) return;
      soundAlertService.playNotificationSound(soundType, null, ringSecondsRef.current);
    }, repeatMs);

    return () => clearInterval(intervalId);
  }, [topExpiresAt, cards.length, arrivedAsMultiple]);

  // Keep "Unattended for X min" labels in the list fresh.
  useEffect(() => {
    if (cards.length === 0) return undefined;
    const intervalId = setInterval(() => setNowTick(Date.now()), 30000);
    return () => clearInterval(intervalId);
  }, [cards.length]);

  // Scroll the Conversation Preview to the bottom (latest message) whenever a card is focused.
  useEffect(() => {
    if (!focusedCard) return;
    const el = previewScrollRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [focusedCard?.cardId]);

  // Fetch the first page of messages for the focused card, same source as the /chat screen.
  useEffect(() => {
    if (!focusedCard) return undefined;

    let cancelled = false;
    const { cardId, conversationId } = focusedCard;

    setPreviewLoadingInitial(true);

    (async () => {
      try {
        const response = await getConversationMessages(conversationId, 1);
        if (cancelled) return;

        const items = sortMessagesByCreatedAt(extractMessagesArray(response));
        const { hasMore } = extractPaginationInfo(response);

        previewPageRef.current = 1;
        setPreviewHasMore(hasMore);
        setFocusedCard((prev) => (prev && prev.cardId === cardId ? { ...prev, messages: items } : prev));

        setTimeout(() => {
          const el = previewScrollRef.current;
          if (el) el.scrollTop = el.scrollHeight;
        }, 0);
      } catch (error) {
        // Keep whatever messages are already there.
      } finally {
        if (!cancelled) setPreviewLoadingInitial(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [focusedCard?.cardId]);

  // After older messages are prepended, restore the scroll offset so the view doesn't jump.
  useEffect(() => {
    const anchor = previewScrollAnchorRef.current;
    if (!anchor) return;
    const el = previewScrollRef.current;
    if (!el) return;

    const timeoutId = setTimeout(() => {
      const heightDifference = el.scrollHeight - anchor.scrollHeight;
      el.scrollTop = anchor.scrollTop + heightDifference;
      previewScrollAnchorRef.current = null;
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [focusedCard?.messages?.length]);

  // Infinite scroll: load older messages when scrolled to the top of the preview.
  const handlePreviewScroll = useCallback(async () => {
    const el = previewScrollRef.current;
    if (!el || !focusedCard || previewLoadingMore || !previewHasMore) return;

    const hasOverflow = el.scrollHeight > el.clientHeight;
    const isAtTop = hasOverflow && el.scrollTop <= 50;
    if (!isAtTop) return;

    const conversationId = focusedCard.conversationId;
    const nextPage = previewPageRef.current + 1;

    previewScrollAnchorRef.current = {
      scrollTop: el.scrollTop,
      scrollHeight: el.scrollHeight,
    };
    setPreviewLoadingMore(true);
    try {
      const response = await getConversationMessages(conversationId, nextPage);
      const newItems = extractMessagesArray(response);
      const { hasMore } = extractPaginationInfo(response);

      previewPageRef.current = nextPage;
      setPreviewHasMore(hasMore);
      if (newItems.length > 0) {
        setFocusedCard((prev) => {
          if (!prev) return prev;
          const merged = [...newItems, ...prev.messages].sort(
            (a, b) => new Date(a.created_at) - new Date(b.created_at)
          );
          return { ...prev, messages: merged };
        });
      } else {
        previewScrollAnchorRef.current = null;
      }
    } catch (error) {
      setPreviewHasMore(false);
      previewScrollAnchorRef.current = null;
    } finally {
      setPreviewLoadingMore(false);
    }
  }, [focusedCard, previewHasMore, previewLoadingMore]);

  // Accept directly from a list row: takes ownership, closes the modal, and opens the
  // conversation in the My tab — same accept flow as the focused view / Review Conversation.
  const handleAcceptFromList = useCallback(
    async (card) => {
      if (submittingCardId) return;
      setSubmittingCardId(card.cardId);
      soundAlertService.cancelAllSounds();
      try {
        const result = await alertsAlarmService.respondToAlertsAlarmCard(card.conversationId, {
          cardId: card.cardId,
          action: 'accept',
        });
        if (result.isSuccess) {
          showSuccess('You have taken ownership of this conversation.');
          const remaining = cardsRef.current.filter(
            (c) => String(c.cardId) !== String(card.cardId)
          );
          if (remaining.length === 0) {
            // Last card in the queue: behave like the single-card flow — open it and close.
            const targetConversationId = card.conversationId;
            navigate('/chat');
            setTimeout(() => {
              window.dispatchEvent(
                new CustomEvent('navigate_to_conversation', {
                  detail: { conversationId: Number(targetConversationId), tabName: ACTIVE_TABS.MY },
                })
              );
            }, 300);
            closeModal();
          } else {
            // Other cards still need attention: just drop this one from the list.
            setCards(remaining);
            setSubmittingCardId(null);
          }
        } else {
          showError(result.error?.message || 'Failed to accept the conversation. Please try again.');
          setSubmittingCardId(null);
        }
      } catch (error) {
        showError('Failed to accept the conversation. Please try again.');
        setSubmittingCardId(null);
      }
    },
    [submittingCardId, closeModal, navigate]
  );

  // Accept from the focused (single-card) view: takes ownership, closes the modal, and
  // opens the conversation in the My tab — mirrors the Review Conversation accept flow.
  const handleAcceptFocused = useCallback(async () => {
    if (!focusedCard || submittingCardId) return;
    setSubmittingCardId(focusedCard.cardId);
    soundAlertService.cancelAllSounds();
    try {
      const result = await alertsAlarmService.respondToAlertsAlarmCard(focusedCard.conversationId, {
        cardId: focusedCard.cardId,
        action: 'accept',
      });
      if (result.isSuccess) {
        showSuccess('You have taken ownership of this conversation.');
        const remaining = cardsRef.current.filter(
          (c) => String(c.cardId) !== String(focusedCard.cardId)
        );
        if (remaining.length === 0) {
          // Last card in the queue: open it and close the modal.
          const targetConversationId = focusedCard.conversationId;
          navigate('/chat');
          setTimeout(() => {
            window.dispatchEvent(
              new CustomEvent('navigate_to_conversation', {
                detail: { conversationId: Number(targetConversationId), tabName: ACTIVE_TABS.MY },
              })
            );
          }, 300);
          closeModal();
        } else {
          // Other cards still need attention: drop this one and return to the list.
          setCards(remaining);
          setFocusedCard(null);
          setSubmittingCardId(null);
        }
      } else {
        showError(result.error?.message || 'Failed to accept the conversation. Please try again.');
        setSubmittingCardId(null);
      }
    } catch (error) {
      showError('Failed to accept the conversation. Please try again.');
      setSubmittingCardId(null);
    }
  }, [focusedCard, submittingCardId, navigate, closeModal]);

  const handleOpenFromList = useCallback((card) => {
    setFocusedCard(toPreviewCard(card));
  }, []);

  const handleBackToList = useCallback(() => {
    setFocusedCard(null);
  }, []);

  const handleViewFullConversation = useCallback(() => {
    navigate('/chat');
  }, [navigate]);

  const handleStopSound = useCallback(() => {
    soundAlertService.cancelAllSounds();
    alarmMutedRef.current = true;
  }, []);

  if (cards.length === 0) return null;

  const showListView = !focusedCard;
  const showNavIcons = !!focusedCard && arrivedAsMultiple;
  const hasTimer = ringCountdown != null;
  const isSubmittingFocused = !!focusedCard && submittingCardId === focusedCard.cardId;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-[100000]" />

      {/* Centered modal */}
      <div className="fixed inset-0 z-[100001] flex items-center justify-center p-3 sm:p-4">
        <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-[31rem] max-h-[90vh] flex flex-col overflow-hidden">

          <div className="review-conversation-modal-scroll flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-4 sm:px-5 pt-6 sm:pt-8 pb-2 text-center">
            {/* Bell icon */}
            <div className="mx-auto mb-2 -mt-2 sm:-mt-4 flex h-12 w-12 sm:h-14 sm:w-14 items-center justify-center rounded-full bg-[#E0E9FD]">
              <Bell className="h-6 w-6 sm:h-7 sm:w-7 text-[#2563eb]" />
            </div>

            <h2 className="text-lg sm:text-xl font-bold text-gray-900">Alert Needs Attention</h2>

            {showListView ? (
              <p className="mt-2 text-[13px] leading-relaxed text-gray-800 w-full max-w-[22rem] mx-auto">
                <span>
                  <span className='font-semibold text-blue-600'>{cards.length}</span>
                  <span className='text-gray-800'>{' '}conversation{cards.length === 1 ? '' : 's'}{' '}
                  {cards.length === 1 ? 'requires' : 'require'} your attention</span>
                </span>
                <br />
                <span className='font-semibold text-gray-'>Please review it.</span>
              </p>
            ) : (
              <p className="mt-3 text-[13px] leading-relaxed text-gray-800 w-full max-w-[20rem] mx-auto">
                <span className="font-semibold text-blue-600">{focusedCard.customerName}</span>{' '}
                {focusedCard.message}
              </p>
            )}

            {showListView ? (
              <div className="mt-5 mb-[-25px] rounded-xl border border-gray-200 text-left overflow-hidden">
                <div className="review-conversation-preview-scroll max-h-[360px] overflow-y-auto overflow-x-hidden">
                  {cards.map((card) => (
                    <AlertCardRow
                      key={card.cardId}
                      card={card}
                      nowTick={nowTick}
                      isSubmitting={submittingCardId === card.cardId}
                      disabled={submittingCardId != null}
                      onAccept={() => handleAcceptFromList(card)}
                      onOpen={() => handleOpenFromList(card)}
                    />
                  ))}
                </div>
              </div>
            ) : (
              <div className="mt-5 rounded-xl border border-gray-200 text-left overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 pb-0">
                  <div className="flex items-center gap-2">
                    <MessageCircle className="w-4 h-4 text-blue-600" />
                    <span className="text-[13px] font-semibold text-gray-900">Conversation Preview</span>
                  </div>
                  {showNavIcons && (
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={handleBackToList}
                        aria-label="Back to list"
                        className="p-1 text-blue-600 hover:text-blue-700"
                      >
                        <ArrowLeft className="w-4 h-4" />
                      </button>
                      {/* <button
                        type="button"
                        onClick={handleViewFullConversation}
                        aria-label="Open full conversation"
                        className="p-1 text-blue-600 hover:text-blue-700"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </button> */}
                    </div>
                  )}
                </div>
                <div
                  ref={previewScrollRef}
                  onScroll={handlePreviewScroll}
                  className="review-conversation-preview-scroll max-h-[38vh] sm:max-h-[330px] overflow-y-auto overflow-x-hidden px-4 py-4 space-y-4"
                  style={{
                    backgroundImage: 'url(/images/with%20background.svg)',
                    backgroundRepeat: 'repeat',
                    backgroundSize: '800px',
                    backgroundPosition: 'center',
                  }}
                >
                  {previewLoadingMore && (
                    <p className="text-xs text-gray-400 text-center py-2">Loading more messages...</p>
                  )}
                  {previewLoadingInitial ? (
                    <div className="flex flex-col items-center justify-center gap-2 text-gray-400 py-8">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span className="text-xs">Loading conversation...</span>
                    </div>
                  ) : focusedCard.messages.length === 0 ? (
                    <p className="text-sm text-gray-400 text-center py-4">No messages to preview.</p>
                  ) : (
                    focusedCard.messages.map((item) => <MessageBubble key={item.id} item={item} />)
                  )}
                </div>
              </div>
            )}

            {hasTimer && !showListView && (
              <p className="pt-[1rem] flex items-center gap-1.5 border-b border-gray-200 pb-4 text-xs text-gray-400">
                <AlarmClock className="w-6 h-6 text-blue-600" />
                <span className="text-gray-800 text-[12px] font-semibold">Next alarm in</span>{' '}
                <span className="font-semibold text-blue-600 text-md">{formatCountdown(ringCountdown)}</span>
              </p>
            )}

            <div className="mt-[1rem] pb-2" />
          </div>

          {/* Fixed footer */}
          <div className="shrink-0 bg-white -mt-[4.5px] px-4 sm:px-5 py-3 sm:py-3">
            {showListView ? (
              <div className="flex items-center justify-between gap-3">
                {hasTimer ? (
                  <p className="flex items-center gap-1.5 text-xs text-gray-500">
                    <AlarmClock className="w-6 h-6 text-blue-600" />
                    <span className="text-gray-800 text-[12px] font-semibold">Next alarm in</span>{' '}
                    <span className="font-semibold text-blue-600">{formatCountdown(ringCountdown)}</span>
                  </p>
                ) : (
                  <span />
                )}
                <button
                  type="button"
                  onClick={handleStopSound}
                  className="shrink-0 rounded-lg bg-blue-600 px-4 py-2 w-xl text-[13px] font-medium text-white transition-colors hover:bg-blue-700"
                >
                  Stop Sound
                </button>
              </div>
            ) : (
              <div className="flex flex-col gap-2.5 sm:gap-3 -mt-[24px]">
                <span className="text-sm font-medium text-gray-800 font-semibold items-center mx-auto">Take action now</span>
                <span className='flex gap-2.5 sm:gap-3'>
                  <button
                  type="button"
                  onClick={handleAcceptFocused}
                  disabled={isSubmittingFocused}
                  className="flex-1 rounded-lg border border-blue-200 bg-white px-3 sm:px-4 py-2.5 sm:py-3 text-sm font-medium text-blue-600 transition-colors hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isSubmittingFocused ? 'Please wait...' : 'Accept Chat'}
                </button>
                <button
                  type="button"
                  onClick={handleStopSound}
                  className="flex-1 rounded-lg bg-blue-600 px-3 sm:px-4 py-2.5 sm:py-3 text-sm font-medium text-white transition-colors hover:bg-blue-700"
                >
                  Stop Sound
                </button>
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default AlertNeedsAttentionModal;
